import { ChevronDownIcon, ChevronRightIcon, pushToDataLayer } from '@arnold/common';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NewReport } from '../../hooks/useNewReport';
import { ReportQuestionCard } from '../ReportQuestionCard';
import { SUMMARY_SENTIMENTS, type SummaryPoint } from './summaryData';
import { CircleButton, Group, Point, PointRow, SentimentDot, SourceQuestions } from './styles';

export function SummaryPoints({
  report,
  points,
  preview = false,
}: {
  report: NewReport;
  points: SummaryPoint[];
  preview?: boolean;
}) {
  const { t } = useTranslation('newReport');
  const visiblePoints = preview
    ? SUMMARY_SENTIMENTS.flatMap((sentiment) => points.filter((point) => point.sentiment === sentiment)).slice(0, 2)
    : points;
  return (
    <>
      {SUMMARY_SENTIMENTS.map((sentiment) => {
        const group = visiblePoints.filter((point) => point.sentiment === sentiment);
        if (!group.length) return null;
        return (
          <Group key={sentiment}>
            <h3>{t(`aiSummary.groups.${sentiment}`)}</h3>
            <ul>
              {group.map((point) => (
                <SummaryPointRow key={point.id} report={report} point={point} preview={preview} />
              ))}
            </ul>
          </Group>
        );
      })}
    </>
  );
}

function SummaryPointRow({ report, point, preview }: { report: NewReport; point: SummaryPoint; preview: boolean }) {
  const { t } = useTranslation('newReport');
  const [expanded, setExpanded] = useState(false);
  const sourcesId = useId();
  const toggle = () => {
    pushToDataLayer({
      event: expanded ? 'ai_recommendation_collapsed' : 'ai_recommendation_expanded',
      hit: {
        event_type: 'generic_event',
        action_name: expanded ? 'collapse' : 'expand',
        report_version: 'new',
        item_index: point.id + 1,
        item_color: point.sentiment === 'negative' ? 'red' : point.sentiment === 'positive' ? 'green' : 'grey',
      },
      _clear: true,
    });
    setExpanded((previous) => !previous);
  };
  return (
    <Point>
      <PointRow>
        <SentimentDot sentiment={point.sentiment} aria-hidden="true" />
        <p>{point.text}</p>
        {!!point.questionIndexes.length && (
          <CircleButton
            type="button"
            disabled={preview}
            tabIndex={preview ? -1 : undefined}
            aria-label={t(expanded ? 'aiSummary.hideSources' : 'aiSummary.showSources')}
            aria-expanded={expanded}
            aria-controls={expanded ? sourcesId : undefined}
            onClick={toggle}
            data-icom="new-report-ai-summary-source-questions"
            data-cy="ai-summary-source-questions"
            data-pptx-hide
          >
            {expanded ? <ChevronDownIcon aria-hidden="true" /> : <ChevronRightIcon aria-hidden="true" />}
          </CircleButton>
        )}
      </PointRow>
      {expanded && !preview && (
        <SourceQuestions id={sourcesId}>
          {point.questionIndexes.map((questionIndex) => (
            <ReportQuestionCard key={questionIndex} report={report} questionIndex={questionIndex} />
          ))}
        </SourceQuestions>
      )}
    </Point>
  );
}
